// @flow

import type { ACFComponentColumn, ACFTabsColumn } from '../../../types';
import type {
  BreakpointWidths,
  ColumnResponsiveData,
  Breakpoint,
} from '../types';

type ResponsiveArray = Array<ACFComponentColumn | ACFTabsColumn>;

/**
 * Initial data for creating the breakpoint data
 */
const breakpointData = {
  width: 0,
  hidden: false,
  hasLeft: true,
  hasRight: true,
  hasTop: true,
  hasBottom: true,
};

/**
 * Set all the breakpoint to default 0
 */
const initialWidths: BreakpointWidths = {
  default: 0,
  sm: 0,
  md: 0,
  lg: 0,
  xl: 0,
};

/**
 * Calculate the full width of the row per breakpoint
 * This is needed to determine the position of the column in the row
 */
const cumulativeColumnWidths = (columns: ResponsiveArray) => (
  columns.reduce((widths: BreakpointWidths, column: ACFComponentColumn | ACFTabsColumn) => ({
    default: widths.default + parseInt(column.responsive.default.width, 10),
    sm: widths.sm + parseInt(column.responsive.sm.width, 10),
    md: widths.md + parseInt(column.responsive.md.width, 10),
    lg: widths.lg + parseInt(column.responsive.lg.width, 10),
    xl: widths.xl + parseInt(column.responsive.xl.width, 10),
  }), initialWidths)
);

/**
 * Set the position data for the given breakpoint
 *
 * @param {number} index Index of the columns in the row
 * @param {number} nbColumns Total number of columns in the row
 * @param {Object} breakpoint Object containg all the breakpoint information
 * @param {number} calculatedColumnsWidth Th ewidth of all the current accumlated row widths
 * @param {number} totalWidth Total row width of the current breakpoint
 * @returns {Breakpoint} Object with all the position information of the column within the row
 */
const calculateBreakpoint = (
  index: number,
  nbColumns: number,
  breakpoint: Object,
  calculatedColumnsWidth: number,
  totalWidth: number,
): Breakpoint => ({
  ...breakpointData,
  hidden: breakpoint.hidden,
  width: parseInt(breakpoint.width, 10),
  hasTop: calculatedColumnsWidth <= 12,
  hasBottom: (totalWidth - calculatedColumnsWidth) < 12,
  hasLeft: (calculatedColumnsWidth - parseInt(breakpoint.width, 10)) % 12 === 0,
  hasRight: (calculatedColumnsWidth % 12 === 0),
});

export const getColumnsResponsiveData = (
  columns: any,
): ColumnResponsiveData => {
  if (!columns) { return [] }

  // If just one column return default values
  const nbColumns = columns.length;
  if (nbColumns === 1) {
    return [{
      default: breakpointData,
      sm: breakpointData,
      md: breakpointData,
      lg: breakpointData,
      xl: breakpointData,
    }];
  }

  // Otherwise calculate the widths
  let widths = { ...initialWidths };
  const columnWidths = cumulativeColumnWidths(columns);

  return columns.map((column, index) => {
    widths = {
      default: widths.default + parseInt(column.responsive.default.width, 10),
      sm: widths.sm + parseInt(column.responsive.sm.width, 10),
      md: widths.md + parseInt(column.responsive.md.width, 10),
      lg: widths.lg + parseInt(column.responsive.lg.width, 10),
      xl: widths.xl + parseInt(column.responsive.xl.width, 10),
    };

    return Object.keys(widths).reduce((breakpointResult, breakpoint) => ({
      ...breakpointResult,
      [breakpoint.valueOf()]: calculateBreakpoint(
        index,
        nbColumns,
        column.responsive[breakpoint],
        widths[breakpoint],
        columnWidths[breakpoint],
      ),
    }), {});
  });
};

export default null;
